import {ViewFilterT} from '@tnc/shared';
import {useCallback, useState} from 'react';
import {useNavigate} from 'react-router';

import {dataTableViewActionT} from '@/lib/types/dataTableView.types';
import {statsRowT} from '@/lib/types/stats.types';
import {BasicDistributorT} from '@/lib/types/user.types';

import {useAuth} from '@/providers/components/authProvider';

import {StatsRowComponent} from '@/components';
import Page from '@/components/Page';
import {DataTableView} from '@/components/dataTableView';

import DistributorSelector from '../rateSheets/components/distributorSelector';

/**
 * Distributor Orders page
 * This page displays all of the orders in the system and allows the admin to view, edit, and delete them
 * @return {JSX.Element} - Distributor Orders page
 */
function DistributorOrders() {
  // Hooks
  const navigate = useNavigate();
  const {
    isAdmin,
    userAccounts: {distributors},
  } = useAuth();

  // State
  const [selectedDistributor, setSelectedDistributor] = useState<BasicDistributorT | null>(null);

  // stats for stats component
  const statsRow: statsRowT = [
    {
      title: 'Total Orders',
      value: 10,
      subtitle: 'This Month',
      icon: 'ri-team-line',
      color: 'primary',
      change: 1.03,
      cardClass: 'Users-main-card primary',
    },
    {
      title: 'Active Orders',
      value: 20,
      subtitle: 'This Month',
      icon: 'ri-user-unfollow-line',
      color: 'secondary',
      change: 0.36,
      cardClass: 'Users-main-card secondary',
    },
    {
      title: 'Inactive Orders',
      value: 50,
      subtitle: 'This Month',
      icon: 'ri-service-line',
      color: 'warning',
      change: -1.28,
      cardClass: 'Users-main-card warning',
    },
  ];

  const actions: dataTableViewActionT<'orders_with_item_count'>[] = [
    {
      icon: 'ri-eye-line',
      color: 'primary',
      handler: row => {
        navigate(`/distributor/orders/${row.order_id}`);
      },
      permission: () => isAdmin,
    },
  ];

  const handleCreateNew = useCallback(() => {
    navigate('/distributor/orders/new');
  }, [navigate]);

  // Data Table View -----------------------------------------------------
  const viewFilters: ViewFilterT<'orders_with_item_count'>[] = [
    {
      column: 'order_type',
      operator: 'eq',
      value: 'Distributor',
    },
    ...(selectedDistributor?.distributor_id
      ? [
          {
            column: 'fk_distributor_id' as const,
            operator: 'eq' as const,
            value: selectedDistributor.distributor_id,
          },
        ]
      : []),
    {
      column: 'date_removed',
      operator: 'is',
      value: null,
    },
  ];

  return (
    <Page currentPage="Distributor Orders">
      <StatsRowComponent stats={statsRow} />

      {/* Moved and restyled distributor selector */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800">Orders Summary</h2>
        <div className="w-72">
          <DistributorSelector
            distributors={[{distributor_id: -1, distributor_name: 'All Distributors'}, ...(distributors || [])]}
            selectedDistributor={selectedDistributor}
            setSelectedDistributor={setSelectedDistributor}
          />
        </div>
      </div>

      <DataTableView
        view="orders_with_item_count"
        title={selectedDistributor?.distributor_name || 'All Distributors'}
        actionsDisplay={actions}
        onAdd={handleCreateNew}
        columnsDisplay={[
          {
            field: 'order_id',
            header: 'Order ID',
          },
          {
            field: 'order_item_count',
            header: 'Item Count',
          },
          {
            field: 'order_status',
            header: 'Order Status',
          },
          {
            field: 'order_delivery_address',
            header: 'Delivery Address',
          },
          {
            field: 'order_delivery_city',
            header: 'City',
          },
          {
            field: 'order_delivery_state',
            header: 'State',
          },
          {
            field: 'order_delivery_postal_code',
            header: 'Postal Code',
          },
          {
            field: 'order_payment_terms',
            header: 'Payment Terms',
          },
          {
            field: 'order_payment_received',
            header: 'Payment Received',
          },
        ]}
        filters={viewFilters}
      />
    </Page>
  );
}

export default DistributorOrders;
